import { Box, InputLabel, TextField as MuiTextField } from '@material-ui/core'
import { LabelField } from '@opus/web.core.form.label-field'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { useField, useFormikContext } from 'formik'
import React, { useCallback, useState, useEffect } from 'react'
import { useDebounce } from 'react-use'
import { FIELD_MODE } from '~/common/constants'
import { useFieldError, useFieldFocused } from '~/common/hooks'
import { useStyles } from './text-field.style'

export const TextField = ({
	name = '',
	validate = () => {},
	label = '',
	placeholder = '',
	mode = FIELD_MODE.edit,
	shrink = true,
	helperText = '',
	endLabel = null,
	...rest
}) => {
	const { t } = useTranslation()
	const classes = useStyles()
	const formik = useFormikContext()

	const [localInputValue, setLocalInputValue] = useState('')
	const [localError, setLocalError] = useState('')

	// Always call useField, but use it conditionally
	const fieldProps = name ? { name, validate: mode === FIELD_MODE.edit && validate } : { name: '', validate: () => {}, value: null }
	const [field, meta] = useField(fieldProps)
	// Always call useFieldError, but use it conditionally
	const formikError = useFieldError(meta)

	const isFormikContext = !!formik

	const inputValue = isFormikContext ? field?.value || '' : localInputValue
	const error = isFormikContext ? formikError : localError

	const updateInputValue = useCallback(
		(value) => {
			if (isFormikContext) {
				field && field.onChange({ target: { name, value } })
			} else {
				setLocalInputValue(value)
			}
		},
		[isFormikContext, field, name]
	)

	const [focused, focusProps] = useFieldFocused(isFormikContext ? field : { onBlur: () => {} })

	useDebounce(
		() => {
			if (isFormikContext && field?.value !== inputValue && !focused) {
				updateInputValue(field?.value || '')
			}
		},
		3000,
		[field?.value, inputValue, focused, isFormikContext]
	)

	useDebounce(
		() => {
			if (isFormikContext) {
				field && field.onChange({ target: { name, value: inputValue } })
			}
		},
		3000,
		[inputValue, isFormikContext]
	)

	useEffect(() => {
		if (!isFormikContext) {
			const validationError = validate(inputValue)
			setLocalError(validationError || '')
		}
	}, [inputValue, validate, isFormikContext])

	const handleChange = useCallback(
		(event) => {
			updateInputValue(event.target.value || '')
		},
		[updateInputValue]
	)

	if (mode === FIELD_MODE.view) {
		return <LabelField classes={{ root: classes.label }} shrink={shrink} label={t(label)} displayValueFormat={() => inputValue} />
	}

	return (
		<>
			<Box display="flex" justifyContent="space-between">
				{label && (
					<InputLabel shrink htmlFor={name} className={classes.label}>
						{t(label)}
					</InputLabel>
				)}
				{endLabel && endLabel}
			</Box>
			<MuiTextField
				id={name}
				placeholder={placeholder && t(placeholder)}
				helperText={error || helperText}
				error={!!error}
				InputProps={focusProps}
				classes={{ root: classes.root }}
				InputLabelProps={{
					shrink: focused || !!inputValue || shrink,
				}}
				{...(isFormikContext ? field : {})}
				value={inputValue}
				onChange={handleChange}
				onBlur={isFormikContext ? field?.onBlur : undefined}
				{...rest}
			/>
		</>
	)
}

TextField.defaultProps = {
	name: '',
	validate: () => {},
	label: '',
	placeholder: '',
	mode: FIELD_MODE.edit,
	shrink: true,
	helperText: '',
	endLabel: null,
}
